"use client";

import {
  Button,
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@oreme/ui";
import { Menu, Ellipsis } from "lucide-react";
import { useTranslations, useLocale } from "next-intl";
import React, { type ReactElement, ElementType, useState } from "react";
import {
  primaryApplications,
  secondaryApplications,
  ICONS,
} from "@/services/applications";

interface GlobalNavProps {
  isMobile?: boolean;
}

function GlobalNav({
  isMobile = false,
}: Readonly<GlobalNavProps>): ReactElement {
  const t = useTranslations("global.Nav");
  const locale = useLocale();
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(false);
  };

  if (isMobile) {
    return (
      <TooltipProvider>
        <Sheet open={open} onOpenChange={setOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <SheetTrigger className="text-muted-foreground" asChild>
                <Button
                  size="icon"
                  variant="outline"
                  aria-label="Menu"
                  className="rounded-full size-8"
                >
                  <Menu strokeWidth={1} size={16} />
                </Button>
              </SheetTrigger>
            </TooltipTrigger>
            <TooltipContent>Menu</TooltipContent>
          </Tooltip>
          <SheetContent
            className="flex flex-col min-w-full min-h-full"
            side="top"
          >
            <SheetHeader>
              <SheetTitle className="sr-only">Menu</SheetTitle>
            </SheetHeader>
            <ul className="flex flex-col list-none gap-2 p-3">
              {primaryApplications.map((application) => {
                const Icon = ICONS[application.icon] as ElementType;
                return (
                  <li key={application.id}>
                    <a
                      className="block p-2 rounded-lg hover:bg-accent"
                      href={application.url}
                      onClick={handleClick}
                    >
                      <div className="flex flex-row gap-2 items-center">
                        {Icon ? <Icon size={20} strokeWidth={1} /> : null}
                        <span className="text-xl font-semibold">
                          {application.shortTitle[locale]}
                        </span>
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>
            <ul className="flex flex-col list-none gap-2 p-8">
              {secondaryApplications.map((application) => {
                const Icon = ICONS[application.icon] as ElementType;
                return (
                  <li key={application.id}>
                    <a
                      className="flex flex-row gap-2 p-1 rounded items-center hover:bg-accent"
                      href={application.url}
                      onClick={handleClick}
                    >
                      {Icon ? <Icon size={20} strokeWidth={1} /> : null}
                      <span className="text-base">
                        {application.shortTitle[locale]}
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </SheetContent>
        </Sheet>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <NavigationMenu id="global-nav" className="hidden md:flex max-w-full">
        <NavigationMenuList className="p-0.5 space-x-0 gap-0.5">
          {primaryApplications.map((application) => {
            const Icon = ICONS[application.icon] as ElementType;
            return (
              <NavigationMenuItem key={application.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <NavigationMenuLink
                      className={`${navigationMenuTriggerStyle()} bg-transparent rounded-full! p-2! h-8!`}
                      asChild
                    >
                      <a
                        href={application.url}
                        aria-label={application.shortTitle[locale]}
                        className="flex flex-row gap-1.5 text-muted-foreground"
                      >
                        {Icon ? <Icon strokeWidth={1} size={16} /> : null}
                        <span className="hidden lg:block text-foreground">
                          {application.shortTitle[locale]}
                        </span>
                      </a>
                    </NavigationMenuLink>
                  </TooltipTrigger>
                  <TooltipContent className="lg:hidden">
                    {application.shortTitle[locale]}
                  </TooltipContent>
                </Tooltip>
              </NavigationMenuItem>
            );
          })}
          <NavigationMenuItem>
            <NavigationMenuTrigger
              aria-label={t("more.title")}
              className="flex justify-center items-center p-2 h-8 gap-1.5 bg-transparent rounded-full [&>svg.lucide-chevron-down]:hidden"
            >
              <Ellipsis size={16} strokeWidth={1} />
              <span className="hidden xl:inline">{t("more.title")}</span>
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <ul className="w-full min-w-180 mx-auto grid gap-3 p-2 lg:grid-cols-2">
                {secondaryApplications.map((application) => {
                  const Icon = ICONS[application.icon] as ElementType;
                  return (
                    <li
                      className="relative p-3 rounded hover:bg-accent"
                      key={application.id}
                    >
                      <a
                        aria-label={application.shortTitle[locale]}
                        href={application.url}
                      >
                        <h4 className="font-semibold inline-flex items-center gap-2">
                          <Icon size={20} strokeWidth={1} />
                          {application.shortTitle[locale]}
                        </h4>
                        <p className="text-sm leading-tight text-muted-foreground">
                          {application.lead[locale]}
                        </p>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>
    </TooltipProvider>
  );
}

export { GlobalNav, type GlobalNavProps };
